/** @module WebSocketClient */
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { tap, retryWhen, delay } from 'rxjs/operators';
import WetSocket from "./socket";
var STATUS_NOT_CONNECTED = 0;
var STATUS_CONNECTED = 1;
var STATUS_RECONNECT = 2;
var STATUS_ERROR = -1;

/**
 * @description 基础类
 * @author 武曲(xiachen.gxc@cainiao.com)
 * @date 2020-03-02
 * @class Wet
 */
var WebSocketClient = /*#__PURE__*/function () {
  /**
   *Creates an instance of Wet.
   * @author 武曲(xiachen.gxc@cainiao.com)
   * @date 2020-03-02
   * @param {*} [opt={}]
   * @memberof WebSocketClient
   */
  function WebSocketClient(opt) {
    if (opt === void 0) {
      opt = {
        url: '',
        name: ''
      };
    }
    /**
     * @description 重连最大次数
     * @static
     * @memberof WebSocketClient
     */
    this.WET_MAX_ATTEMPTS = 10;
    /**
     * @description 状态
     * @memberof WebSocketClient
     */
    this.status = new BehaviorSubject(STATUS_NOT_CONNECTED);
    /**
     * @description 当前重试次数
     * @memberof WebSocketClient
     */
    this.attemptTimes = new BehaviorSubject(0);
    /**
     * @description 模块名称，用于日志
     * @memberof WebSocketClient
     */
    this.name = void 0;
    /**
     * @description 接口地址
     * @memberof WebSocketClient
     */
    this.url = void 0;
    /**
     * @description 给wet发送请求的对象
     * @memberof WebSocketClient
     */
    this.input = void 0;
    /**
     * @description wet当前版本
     * @memberof WebSocketClient
     */
    this.version = '0.0.0';
    /**
     * @description wet当前umid
     * @memberof WebSocketClient
     */
    this.umid = '';
    this.connectionStatus = void 0;
    this.messages = void 0;
    this.wetAttemptTimes = new BehaviorSubject(0);
    var _opt = opt,
      _opt$url = _opt.url,
      url = _opt$url === void 0 ? 'wss://localhost:8080' : _opt$url,
      _opt$name = _opt.name,
      name = _opt$name === void 0 ? 'unknow websocket client' : _opt$name;
    this.name = name;
    this.url = url;
    this._init();
  }

  /**
   * @description 初始化函数
   * @author 武曲(xiachen.gxc@cainiao.com)
   * @date 2020-03-02
   * @memberof WebSocketClient
   * @private
   */
  var _proto = WebSocketClient.prototype;
  _proto._init = function _init() {
    var _this = this;
    this.input = new Subject();
    // this.input = new Subject(0); TS 报错构造函数没有入参，暂时去除

    var _WetSocket = WetSocket({
        url: this.url,
        input: this.input
      }),
      messages = _WetSocket.messages,
      connectionStatus = _WetSocket.connectionStatus;
    this.connectionStatus = connectionStatus;
    this.messages = messages.pipe(retryWhen(function (err) {
      return err.pipe(tap(function () {
        var _this$wetAttemptTimes, _this$wetAttemptTimes2;
        _this.status.next(STATUS_RECONNECT);
        if (((_this$wetAttemptTimes = _this.wetAttemptTimes) === null || _this$wetAttemptTimes === void 0 ? void 0 : (_this$wetAttemptTimes2 = _this$wetAttemptTimes.getValue) === null || _this$wetAttemptTimes2 === void 0 ? void 0 : _this$wetAttemptTimes2.call(_this$wetAttemptTimes)) >= _this.WET_MAX_ATTEMPTS) {
          var _this$wetAttemptTimes3, _this$wetAttemptTimes4;
          (_this$wetAttemptTimes3 = _this.wetAttemptTimes) === null || _this$wetAttemptTimes3 === void 0 ? void 0 : (_this$wetAttemptTimes4 = _this$wetAttemptTimes3.next) === null || _this$wetAttemptTimes4 === void 0 ? void 0 : _this$wetAttemptTimes4.call(_this$wetAttemptTimes3, STATUS_ERROR);
          _this.logger('连接websocket服务失败');
          throw new Error('连接websocket服务失败');
        } else {
          var _this$wetAttemptTimes5, _this$wetAttemptTimes6, _this$wetAttemptTimes7;
          (_this$wetAttemptTimes5 = _this.wetAttemptTimes) === null || _this$wetAttemptTimes5 === void 0 ? void 0 : _this$wetAttemptTimes5.next(_this.wetAttemptTimes.getValue() + 1);
          _this.logger("websocket\u8FDE\u63A5\u53D1\u751F\u9519\u8BEF\uFF0C\u5C1D\u8BD5\u91CD\u65B0\u8FDE\u63A5\u4E2D, \u5F53\u524D\u7B2C " + (((_this$wetAttemptTimes6 = _this.wetAttemptTimes) === null || _this$wetAttemptTimes6 === void 0 ? void 0 : (_this$wetAttemptTimes7 = _this$wetAttemptTimes6.getValue) === null || _this$wetAttemptTimes7 === void 0 ? void 0 : _this$wetAttemptTimes7.call(_this$wetAttemptTimes6)) || 0) + " \u6B21\u5C1D\u8BD5");
        }
      }), delay(5000));
    })).pipe(tap(function () {
      _this.status.next(STATUS_CONNECTED);
    }));
  }

  /**
   * @description 日志
   * @author 武曲(xiachen.gxc@cainiao.com)
   * @date 2020-03-02
   * @param {*} message
   * @param {string} [type='normal']
   * @memberof WebSocketClient
   */;
  _proto.logger = function logger(message, type) {
    if (type === void 0) {
      type = 'normal';
    }
    var messageWithName = "[" + this.name + "]: " + message;
    switch (type) {
      case 'normal':
        {
          console.log(messageWithName); // eslint-disable-line
          break;
        }
      case 'warn':
        {
          console.warn(messageWithName); // eslint-disable-line
          break;
        }
      case 'error':
        {
          console.error(messageWithName); // eslint-disable-line
          break;
        }
      case 'debug':
        {
          console.debug(messageWithName); // eslint-disable-line
          break;
        }
      default:
        {
          console.log(messageWithName); // eslint-disable-line
        }
    }
  };
  _proto.stringify = function stringify() {
    return function (source) {
      return new Observable(function (observer) {
        return source.subscribe({
          next: function next(x) {
            observer.next(JSON.stringify(x));
          },
          error: function error(err) {
            observer.error(err);
          },
          complete: function complete() {
            observer.complete();
          }
        });
      });
    };
  };
  _proto.isWetReady = function isWetReady() {
    return this.status.getValue() === STATUS_CONNECTED;
  };
  return WebSocketClient;
}();
/** 模块导出 */
export default WebSocketClient;